import { useEffect, useState } from "react";
import { ScrollText } from "lucide-react";
import toast from "react-hot-toast";
import { PageLoader } from "@/components/brand/PageLoader";
import { supabase } from "@/lib/supabase";

interface AuditRow {
  id: number;
  actor_email: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

/**
 * Admin → Audit log. Read-only table of every service-role write
 * (venue approval, rejection, Stripe event, etc). Useful for
 * forensics if an admin account is ever compromised - we can see
 * what was done from where.
 */
export function AdminAuditLog() {
  const [rows, setRows] = useState<AuditRow[] | null>(null);

  useEffect(() => {
    supabase
      .from("audit_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (error) {
          console.error("[AdminAuditLog] load failed", error);
          toast.error(`Couldn't load audit log: ${error.message}`);
          setRows([]);
          return;
        }
        setRows((data ?? []) as AuditRow[]);
      });
  }, []);

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Audit log
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Audit log</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every admin-authored change is recorded here. Read-only: even
          admins can't modify or delete audit entries.
        </p>
      </header>

      {rows === null ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 py-12 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-volt-subtle text-volt">
            <ScrollText className="h-5 w-5" />
          </div>
          <p className="text-sm font-semibold">No admin activity yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Approvals, rejections, and Stripe events will appear here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Who</th>
                <th className="px-4 py-2 text-left">Action</th>
                <th className="px-4 py-2 text-left">Entity</th>
                <th className="px-4 py-2 text-left">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border align-top last:border-b-0">
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.actor_email ?? (
                      <span className="text-muted-foreground">system</span>
                    )}
                    {r.ip_address && (
                      <p className="text-[10px] text-muted-foreground">
                        {r.ip_address}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
                      {r.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <p>{r.entity_type}</p>
                    {r.entity_id && (
                      <p className="text-[10px] text-muted-foreground">
                        {r.entity_id.slice(0, 8)}…
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-muted-foreground">
                    {r.details
                      ? Object.entries(r.details).map(([k, v]) => (
                          <div key={k}>
                            <span className="font-semibold">{k}:</span>{" "}
                            {String(v)}
                          </div>
                        ))
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
